import Hls, { HlsConfig } from 'hls.js';
import Player from 'video.js/dist/types/player';
import { HlsJsPreset } from '../../types';
import { AggressivePlaylistLoader } from './aggressive-playlist-loader';

const baseConfig: Partial<HlsConfig> = {
  enableWorker: true, // keep main thread snappy
};

const LIVE_EDGE_OFFSET = 30; // 40 seconds from the end of the playlist
/**
 * Live stream preset for HLS.js
 * Optimized for low-latency live streaming with aggressive updates
 */
export const livePreset: HlsJsPreset = {
  config: {
    ...baseConfig,

    liveDurationInfinity: true,

    // 1. STOP THE RACE CONDITION
    autoStartLoad: false,

    // 2. ENABLE AGGRESSIVE UPDATES
    lowLatencyMode: true,

    // 3. DISABLE PREFETCH
    startFragPrefetch: false,

    // 4. THE FIX: DEFINE THE TARGET
    // We target 1.5 segments (45s) behind live.
    liveSyncDuration: 40,

    // 5. RELAX LATENCY CHECKS
    // We allow drifting up to 3 segments (90s) behind.
    // Since 3 > 1.5, this is now VALID.
    liveMaxLatencyDuration: 80,

    maxBufferLength: 35,
    backBufferLength: 0,

    // @ts-ignore: Types can be strict about the constructor signature
    pLoader: AggressivePlaylistLoader,
  },
  setup: (hls: Hls, player: Player) => {
    const videoEl = player.el() as HTMLMediaElement;

    // -----------------------------------------------------------
    // HACK: Force Aggressive Playlist Polling
    // -----------------------------------------------------------
    hls.on(Hls.Events.LEVEL_LOADED, (event, data) => {
      // Standard HLS.js uses the manifest's TARGETDURATION (31s) to schedule
      // the next reload. We overwrite this to 10s to force it to check faster.
      if (data.details) {
        data.details.targetduration = 10;
      }
    });

    // Setup live-specific event handlers
    hls.once(Hls.Events.MANIFEST_PARSED, (event, data) => {
      const level = hls.levels[hls.firstLevel];
      if (!level?.details) {
        hls.startLoad();
        return;
      }

      // Also apply the hack to the first load so the SECOND load happens fast
      level.details.targetduration = 10;

      const fragments = level.details.fragments;
      if (fragments.length <= 1) return;

      // 1. Calculate Live Edge (End of Playlist)
      const latestFragment = fragments[fragments.length - 1];
      const playlistEndTime = latestFragment.start + latestFragment.duration;

      // 2. Target 15s from the end (Middle of Segment 3)
      const targetTime = Math.max(0, playlistEndTime - LIVE_EDGE_OFFSET);

      // 3. FORCE LOAD
      hls.startLoad(targetTime);

      // 4. SYNC & PLAY (This replaces the 'autoplay' functionality)
      videoEl.currentTime = targetTime;

      if (player.autoplay()) {
        player.play(); // <--- We trigger play HERE, not in config
      }
    });
  },
};
